// ─── Geste de retour par balayage (bord gauche) ───────────────────
// Principe de navigation app-wide : un balayage depuis le bord GAUCHE = retour
// d'un niveau, comme le bouton ‹ de l'en-tête, avec l'animation d'iOS (la page
// SUIT le doigt ; au relâchement, franchie au tiers → retour, sinon ressort).
//
//   <GesteRetourEcran actif={!!retour} onRetour={retour}>{contenu}</GesteRetourEcran>
//
// Le geste n'est armé que dans les 40 premiers pixels (hitSlop) : le reste de
// l'écran garde ses balayages horizontaux (swipe d'une ligne, onglets…). Sur le
// web, pas de geste (retour = navigateur) — rendu transparent.
import React, { useRef, useMemo } from 'react';
import { Animated, View, Platform, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';

const NATIF = Platform.OS !== 'web';

// `dansModale` : le contenu d'une <Modal> vit dans une hiérarchie native séparée,
// hors de portée du GestureHandlerRootView de l'App → il lui faut le sien.
export default function GesteRetourEcran({ actif, onRetour, children, style, dansModale = false }) {
  const { width } = useWindowDimensions();
  const x = useRef(new Animated.Value(0)).current;

  const geste = useMemo(() => Gesture.Pan()
    .enabled(!!actif && !!onRetour)
    .runOnJS(true)
    .hitSlop({ left: 0, width: 40 })   // bord gauche uniquement
    .activeOffsetX(15)
    .failOffsetY([-25, 25])
    .onUpdate((e) => { x.setValue(Math.max(0, e.translationX)); })   // jamais vers la gauche
    .onEnd((e) => {
      // Effectif au-delà de 25 % de la largeur (ou lancer rapide).
      const franchi = e.translationX > width * 0.25 || e.velocityX > 800;
      if (franchi) {
        Animated.timing(x, { toValue: width, duration: 200, useNativeDriver: NATIF })
          .start(() => { x.setValue(0); onRetour?.(); });
      } else {
        Animated.spring(x, { toValue: 0, useNativeDriver: NATIF, speed: 14, bounciness: 3 }).start();
      }
    }), [actif, onRetour, width, x]);

  // Sur le web, le retour est celui du navigateur : pas de geste.
  if (Platform.OS === 'web') return <View style={[{ flex: 1 }, style]}>{children}</View>;

  const contenu = (
    <GestureDetector gesture={geste}>
      <Animated.View style={[{ flex: 1 }, style, { transform: [{ translateX: x }] }]} collapsable={false}>
        {children}
      </Animated.View>
    </GestureDetector>
  );
  return dansModale ? <GestureHandlerRootView style={{ flex: 1 }}>{contenu}</GestureHandlerRootView> : contenu;
}
